import { useEffect, useState } from 'react'

import { Button, FormErrorsType, FormWarningsType, IconAlertTriangle, Input, Popup, Select } from '@/components'
import { InverterManufacturer, Product, Warranty } from '@/models'
import { FormChangeEventHandler, JsonDataType, IdType } from '@/types'

export interface BatteryFormDataType extends JsonDataType {
    id: IdType
    product_id: IdType
    size_kw: string | number
    serial_number: string[]
    product?: {
        id: IdType
        model_name: string
    }
}

type BatteryFormProps = {
    form: BatteryFormDataType
    products?: Product[]
    onChange: FormChangeEventHandler
    inputClassName?: string
    labelPrefix?: string
    errors: FormErrorsType
    warnings: FormWarningsType
    warranty?: Warranty
}

export const BatteryForm = ({
    form,
    inputClassName,
    products,
    errors,
    warnings,
    labelPrefix = '',
    warranty,
    ...props
}: BatteryFormProps) => {
    const [showDINGuide, swtShowDINGuide] = useState(false)
    const battery = products?.find(({ id }) => id === parseInt(form.product_id as string))
    const availableSizes = battery?.properties
        .find(item => item.property.name === 'available_sizes')?.value as string[] || []

    const capacityOptions = [{
        value: '',
        title: ''
    }, ...availableSizes.map((item: string) => ({
        value: parseFloat(item),
        title: Warranty.getField('size_kw')
            .format(item)
    }))]

    useEffect(() => {
        if (capacityOptions.every(({ value }) => value.toString() !== form.size_kw.toString())) {
            props.onChange({ target: { value: '', name: 'size_kw' } })
        }
    }, [form.product_id])

    const isTesla = !!battery?.model_name.toLowerCase().includes('tesla')

    return <>
        <Select
            id="product_id"
            name="product_id"
            label={`${labelPrefix} Make & Model*`}
            options={[{ title: '', value: '' }, ...products?.map(({ id, model_name: title }) => ({
                value: id,
                title
            })) || []]}
            onChange={props.onChange}
            value={form.product_id}
            errors={errors.product_id}
            className={inputClassName}
            data-test="battery-product-id"
        />
        <Select
            id="size_kw"
            name="size_kw"
            label={`${labelPrefix} Capacity (kWh)*`}
            options={capacityOptions}
            onChange={props.onChange}
            value={form.size_kw}
            errors={errors.size_kw}
            disabled={!form.product_id}
            className={inputClassName}
            data-test="battery-size-kw"
        />
        <Input
            id="serial_number"
            name="serial_number"
            label={`${labelPrefix} ${isTesla ? 'Tesla DIN' : 'Serial Number'}`}
            onChange={e => {
                let value = e.target.value as string
                if (value.endsWith(',') && form.serial_number.at(-1) === '') {
                    value = value.slice(0, -1)
                }
                props.onChange({
                    ...e,
                    target: {
                        name: e.target.name,
                        value: value.trim() ? value.split(',').map(item => item.trim()) : []
                    }
                })
            }}
            value={form.serial_number.join(', ')}
            errors={Object.keys(errors).reduce((acc, key) =>
                key.startsWith('serial_number')
                    ? [...acc, (errors[key] as string).replace(/\.([0-9]+)/, (_, num) =>
                        ` #${1 + Number(num)}`)]
                    : acc
            , [] as string[])}
            warnings={warnings.serial_number}
            disabled={!form.product_id}
            className={inputClassName}
            placeholder={isTesla
                ? 'DIN or multiple, comma-separated'
                : 'SN or multiple, comma-separated'}
            uppercase={!warranty && isTesla}
            mask={!warranty && isTesla
                ? {
                    mask: Array(form.serial_number.length + 1)
                        .fill('0000000{-}00{-}a{--}**************')
                        .join('{, }'),
                    definitions: { '*': /[A-Za-z0-9]/ }
                }
                : undefined}
            data-test="battery-serial-number"
            postIcon={warnings.serial_number &&
                <IconAlertTriangle className="stroke-warning-500" size="sm"/>}
            hint={isTesla &&
                <Button
                    design="btn-link"
                    type="button"
                    className="text-primary-700"
                    onClick={() => swtShowDINGuide(true)}
                >
                    How to Find Tesla DIN
                </Button>}
        />

        {battery && <Popup
            className="w-200"
            open={showDINGuide}
            onClose={() => swtShowDINGuide(false)}
        >
            {InverterManufacturer.tesla.systemIdGuide}
        </Popup>}
    </>
}
